import type { FieldProvenanceDto, MissionAssessmentTechnologyUseCreateDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { useGetDomains } from '@queries/domain.query';
import { useGetUses } from '@queries/use.query';
import { missionAssessmentTechnologyUseCreateSchema } from '@schemas/mission-assessment.schema';
import { sectionProvenance } from '@utils/mission-assessment-provenance.util';
import { EditIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod/v4';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';
import { MissionAssessmentFormRecommendedTechUsesModal } from './mission-assessment-form-recommended-tech-uses-modal.component';

export const MissionAssessmentFormRecommendedTechUses = withForm({
  ...missionAssessmentDraftFormOptions,
  props: {
    provenance: undefined as Record<string, FieldProvenanceDto> | null | undefined,
  },
  render: function Render({ form, provenance }) {
    const { data: availableUses } = useGetUses();
    const { data: availableDomains } = useGetDomains();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [techUseToEdit, setTechUseToEdit] = useState<{
      index: number;
      techUse: MissionAssessmentTechnologyUseCreateDto;
    } | null>(null);

    const getUseName = (useId: string) => {
      return availableUses?.find((u) => u.id === useId)?.name || 'Deleted Use';
    };

    const getDomainName = (domainId: string) => {
      return availableDomains?.find((d) => d.id === domainId)?.name || 'Deleted Domain';
    };

    const handleAddTechUse = () => {
      setTechUseToEdit(null);
      setIsModalOpen(true);
    };

    const handleEditTechUse = (techUse: MissionAssessmentTechnologyUseCreateDto, index: number) => {
      setTechUseToEdit({ index, techUse });
      setIsModalOpen(true);
    };

    const closeModal = () => {
      setIsModalOpen(false);
      setTechUseToEdit(null);
    };

    const handleTechUseSubmit = (techUseData: MissionAssessmentTechnologyUseCreateDto) => {
      // Normalize the data to match form's expected shape
      const normalizedData = {
        description: techUseData.description ?? '',
        domains: techUseData.domains,
        useId: techUseData.useId,
      };

      if (techUseToEdit !== null) {
        form.setFieldValue(`recommendedTechnologyUses[${techUseToEdit.index}]`, normalizedData);
      } else {
        form.setFieldValue('recommendedTechnologyUses', (prev) => [...(prev || []), normalizedData]);
      }
      closeModal();
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-medium text-gray-700">
            Recommended Technology Uses
            <FieldProvenanceBadge
              context="form"
              provenance={sectionProvenance(provenance, 'RECOMMENDED_TECHNOLOGY_USES')}
            />
          </h3>
        </div>

        <form.AppField
          mode="array"
          name="recommendedTechnologyUses"
          validators={{
            onChange: z.array(missionAssessmentTechnologyUseCreateSchema),
          }}
        >
          {(field) => (
            <div className="space-y-3">
              {field.state.value?.length ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {field.state.value.map((techUse, index) => {
                    const useName = techUse?.useId ? getUseName(techUse.useId) : 'Select use';
                    const domains = techUse?.domains || [];
                    const domainNames = domains
                      .map((d) => d.domainId)
                      .filter((domainId) => Boolean(domainId))
                      .map((domainId) => getDomainName(domainId));

                    return (
                      <div
                        className="rounded-lg border border-gray-200 bg-white p-3"
                        key={`${techUse?.useId || 'use'}-${index}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-600">{useName}</div>
                            <BlueLabel text="Use" />

                            {domainNames.length ? (
                              <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600">
                                {domainNames.join(', ')}
                              </div>
                            ) : (
                              <div className="mt-2 text-xs text-gray-500">No domains selected</div>
                            )}
                            <BlueLabel text="Domains" />
                          </div>

                          <div className="flex items-center gap-2">
                            <IconButton
                              ariaLabel="Edit technology use"
                              icon={<EditIcon size={16} />}
                              onClick={() =>
                                handleEditTechUse(techUse as MissionAssessmentTechnologyUseCreateDto, index)
                              }
                              size="md"
                              variant="secondary"
                            />
                            <IconButton
                              ariaLabel="Delete technology use"
                              icon={<TrashIcon size={16} />}
                              onClick={() => field.removeValue(index)}
                              size="md"
                              variant="secondary"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <Button
                className="h-auto self-start"
                onClick={handleAddTechUse}
                type="button"
                variant="link"
              >
                <PlusIcon className="size-4" />
                <BlueLabel text="Add Technology Use" />
              </Button>

              {isModalOpen && (
                <MissionAssessmentFormRecommendedTechUsesModal
                  isOpen={isModalOpen}
                  onClose={closeModal}
                  onSubmit={handleTechUseSubmit}
                  techUseToEdit={techUseToEdit?.techUse || null}
                />
              )}
            </div>
          )}
        </form.AppField>
      </div>
    );
  },
});
